import config from './config';
import { refreshTokenQuietly } from './api';

const POLL_TIMEOUT = 40000; // ms — abort a poll request stuck past the server's ~25s hold
const POLL_ERROR_RETRY_DELAY = 5000; // ms between poll attempts after an error
const MAX_POLL_FAILURES = 3; // consecutive poll failures before reporting the fallback broken

// The socket server's plain-HTTP base URL (health probe, long-poll fallback).
export const socketHTTPBase = () =>
  config.socketServer.replace(/^ws/, 'http').replace(/\/+$/, '');

/*
 * HTTP long-polling fallback for a socket room, engaged when WebSockets are
 * blocked (VPN/firewall/corporate proxy) but the socket server is reachable
 * over plain HTTP. This class owns the transport only: the polling loop, the
 * poll_id lifecycle (including the explicit leave on stop), quiet token
 * refresh on 401 and failure counting. What "healthy" and "broken" mean for
 * the app (redux state, banners) is the caller's business, via callbacks:
 *
 *   onEvent(event, data)    — a server event arrived
 *   onHealthy()             — called after every successful poll
 *   onBroken(authFailure)   — MAX_POLL_FAILURES consecutive failures;
 *                             authFailure is true when the last failure was a
 *                             401 (auth/permission problem, not transport)
 *
 * The loop keeps retrying after onBroken and reports onHealthy on recovery;
 * it only ends on stop().
 */
export class PollFallback {
  constructor({ label, onEvent, onHealthy, onBroken }) {
    this.label = label; // for log messages ('project'/'user')
    this.onEvent = onEvent;
    this.onHealthy = onHealthy;
    this.onBroken = onBroken;
    this.polling = false;
    this.healthy = false; // at least one successful poll since (re)start
    this._room = null;
    this._pollId = null;
    this._abort = null;
    // Incremented on each start so a stale loop that was mid-await when
    // polling was stopped/restarted exits instead of running twice.
    this._gen = 0;
  }

  start(room) {
    if (this.polling) return;
    this.polling = true;
    this.healthy = false;
    this._room = room;
    this._gen++;
    console.warn(
      `[Socket] ${this.label} starting long-poll fallback for room ${room}`
    );
    this._loop(this._gen);
  }

  stop() {
    if (!this.polling) return;
    console.debug(`[Socket] ${this.label} stopping long-poll fallback`);
    this.polling = false;
    this.healthy = false;
    if (this._abort) {
      this._abort.abort();
      this._abort = null;
    }
    if (this._pollId && this._room) {
      // Tell the server we're leaving so the user drops out of the editors
      // list immediately instead of lingering until the server's idle-client
      // reaper fires. Fire-and-forget: if it's lost, the reaper still cleans
      // up. keepalive lets the request survive tab navigation/close.
      const params = new URLSearchParams({
        room: this._room,
        poll_id: this._pollId,
        leave: '1',
      });
      fetch(`${socketHTTPBase()}/poll?${params.toString()}`, {
        credentials: 'include',
        cache: 'no-store',
        keepalive: true,
      }).catch(() => {});
    }
    this._pollId = null;
    this._room = null;
  }

  _stale(gen) {
    return !this.polling || gen !== this._gen;
  }

  async _loop(gen) {
    let failures = 0;
    let authFailure = false; // was the most recent failure a 401?
    while (!this._stale(gen)) {
      try {
        authFailure = false;
        const params = new URLSearchParams({ room: this._room });
        if (this._pollId) {
          params.set('poll_id', this._pollId);
        }
        this._abort = new AbortController();
        const timer = setTimeout(() => this._abort.abort(), POLL_TIMEOUT);
        let resp;
        try {
          resp = await fetch(`${socketHTTPBase()}/poll?${params.toString()}`, {
            credentials: 'include',
            cache: 'no-store',
            signal: this._abort.signal,
          });
        } finally {
          clearTimeout(timer);
        }
        if (this._stale(gen)) break;
        if (resp.status === 401) {
          // Token rotated/expired between polls: quiet refresh, then retry
          // (failure counting in the catch below paces a genuinely dead
          // session instead of looping on /refresh).
          authFailure = true;
          await refreshTokenQuietly().catch(() => {});
          throw new Error('poll unauthorized');
        }
        if (!resp.ok) {
          throw new Error(`poll failed with status ${resp.status}`);
        }
        const body = await resp.json();
        this._pollId = body.poll_id;
        failures = 0;
        if (!this.healthy) {
          this.healthy = true;
          console.info(
            `[Socket] ${this.label} live updates flowing via long-poll fallback`
          );
        }
        this.onHealthy();
        for (const evt of body.events || []) {
          try {
            this.onEvent(evt.event, evt.data);
          } catch (e) {
            console.warn('[Socket] Error handling polled event', e, evt);
          }
        }
      } catch (e) {
        if (this._stale(gen)) break;
        failures++;
        console.warn(
          `[Socket] ${this.label} poll error (${failures}/${MAX_POLL_FAILURES}):`,
          e
        );
        if (failures >= MAX_POLL_FAILURES) {
          this.healthy = false;
          this.onBroken(authFailure);
        }
        await new Promise((resolve) =>
          setTimeout(resolve, POLL_ERROR_RETRY_DELAY)
        );
      }
    }
  }
}
